import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Pagination,
  Button,
} from "@mui/material";
import CollapsibleFormElement from "../../common/collapsible-form-element";
import { styled } from "@mui/material/styles";
import { useAuthStore } from "../../../authProvider";

interface ExpressionItem {
  expression: string;
  checked: boolean;
  initialOrder: number;
  matchPercentage?: number;
}

interface ExpressionSelectorProps {
  expressions: ExpressionItem[];
  isLoading: boolean;
  validationErrors: Record<string, string>;
  onChange: (expressions: ExpressionItem[]) => void;
  onFetchExpressions: () => void;
}

const VirtualizedContainer = styled(Box)(() => ({
  height: "100%",
  width: "100%",
  overflow: "hidden",
  position: "relative",
}));

const ITEMS_PER_PAGE = 150; // Number of expressions to show per page

const ExpressionSelector: React.FC<ExpressionSelectorProps> = ({
  expressions,
  isLoading,
  validationErrors,
  onChange,
  onFetchExpressions,
}) => {
  const { user } = useAuthStore();

  const isClientRelated = user?.isClientRelated;

  const [page, setPage] = useState(1);

  // Sort expressions first (checked items at the top) and then calculate pagination
  const sortedExpressions = useMemo(() => {
    // Create a new array to avoid mutating the original
    return [...expressions];
  }, [expressions]);

  // Calculate pagination using sorted expressions
  const totalPages = Math.ceil(sortedExpressions.length / ITEMS_PER_PAGE);
  const paginatedExpressions = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return sortedExpressions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedExpressions, page]);

  // Simple handle click function
  const handleChipClick = (clickedExpression: string) => {
    // Map to a completely new array with updated values
    const newExpressions = expressions.map((item) => {
      if (item.expression === clickedExpression) {
        return { ...item, checked: !item.checked };
      }
      return item;
    });

    // Send the new array to parent
    onChange(newExpressions);
  };

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelectAll = () => {
    onChange(expressions.map((item) => ({ ...item, checked: true })));
  };

  const handleSelectNone = () => {
    onChange(expressions.map((item) => ({ ...item, checked: false })));
  };

  const allSelected = expressions.every((item) => item.checked);

  return (
    <CollapsibleFormElement
      title="Expression Selection"
      hasError={!!validationErrors.expressions}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {expressions.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={allSelected ? handleSelectNone : handleSelectAll}
                size="small"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            )}
            {!isClientRelated && (
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await onFetchExpressions();
                  await setPage(1);
                }}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                size="small"
              >
                Sort Expressions
              </Button>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <VirtualizedContainer
              sx={{ py: 1, maxHeight: 500, overflow: "auto" }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                {paginatedExpressions.map((item) => (
                  <Chip
                    key={item.expression}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          sx={{ fontSize: "0.85rem", fontWeight: 500 }}
                        >
                          {item.expression}
                        </Typography>
                        {!isClientRelated && (
                          <Typography sx={{ fontSize: "0.75rem" }}>
                            ({item.matchPercentage || 0}%)
                          </Typography>
                        )}
                      </Box>
                    }
                    color={item.checked ? "primary" : "default"}
                    variant={item.checked ? "filled" : "outlined"}
                    onClick={() => handleChipClick(item.expression)}
                    clickable
                    sx={{
                      margin: 0.5,
                      maxWidth: "100%",
                      height: "auto",
                      borderWidth: 1,
                      fontWeight: 400,
                      color: item.checked ? "primary.dark" : "text.primary",
                      bgcolor: item.checked
                        ? "rgba(25, 118, 210, 0.08)"
                        : "transparent",
                      boxShadow: item.checked
                        ? "0 1px 2px rgba(0,0,0,0.1)"
                        : "none",
                      transition: "all 0.2s ease",
                      px: 1.2,
                      py: 0.6,
                      "& .MuiChip-label": {
                        padding: "1px 4px",
                        fontSize: "0.9rem",
                        lineHeight: 1.5,
                      },
                      "&:hover": {
                        bgcolor: item.checked
                          ? "rgba(25, 118, 210, 0.12)"
                          : "rgba(0, 0, 0, 0.03)",
                      },
                    }}
                  />
                ))}
              </Box>
            </VirtualizedContainer>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Box>
      {validationErrors.expressions && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {validationErrors.expressions}
        </Typography>
      )}
    </CollapsibleFormElement>
  );
};

export default ExpressionSelector;
